// Widget: Fortschritt — eigene aktive Projekte auf einen Blick, sortiert nach
// Fortschritt (am wenigsten fertige Projekte zuerst, damit sie auffallen).
// Fortschritt kommt aus der serverseitig konsolidierten Zählung (Ticket #877,
// tasks_total/tasks_done je Projekt) — keine eigene Zählung im Client.

import { Link } from "wouter-preact";
import { useProjects } from "../../store/projects";
import { useUserPrefs } from "../../store/userPrefs";
import { isProjectVisible } from "../../lib/departmentFilter";

export function ProgressWidget(): preact.JSX.Element {
  const projects = useProjects();
  const prefs = useUserPrefs();

  if (projects.loading)
    return <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>;

  const visible = projects.items.filter(
    (p) => isProjectVisible(p, prefs.visible_departments) && p.status === "active"
  );

  if (visible.length === 0)
    return (
      <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
        Keine aktiven Projekte mit Fortschritt.
      </p>
    );

  const rows = visible
    .map((p) => {
      const total = p.tasks_total;
      const done = p.tasks_done;
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      return { project: p, pct };
    })
    .sort((a, b) => a.pct - b.pct || a.project.name.localeCompare(b.project.name));

  return (
    <ul class="flex flex-col gap-3">
      {rows.map(({ project, pct }) => (
        <li key={project.id}>
          <Link
            href={`/p/${project.id}`}
            class="block rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 group"
          >
            <div class="flex items-center justify-between gap-2 mb-1">
              <span class="text-xs font-medium text-[color:var(--color-on-surface)] truncate group-hover:underline">
                {project.name}
              </span>
              <span class="text-label text-[color:var(--color-on-surface-variant)] shrink-0">
                {pct}%
              </span>
            </div>
            <div
              role="progressbar"
              aria-label={`Fortschritt ${project.name}`}
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              class="h-1.5 rounded bg-[color:var(--color-surface-container-low)] overflow-hidden"
            >
              <div
                class="h-full rounded"
                style={{ width: `${pct}%`, background: project.color ?? "var(--color-primary)" }}
                aria-hidden="true"
              />
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
}
